import React, { useMemo, useState } from "react";
import { useSite } from "@/context/SiteContext";
import { ChevronsUpDown, Check, Globe, Search } from "lucide-react";

export default function SiteSwitcher() {
  const { sites, current, currentId, setCurrentId, loading } = useSite();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const grouped = useMemo(() => {
    const filtered = q
      ? sites.filter((s) =>
          (s.name + " " + (s.group || "")).toLowerCase().includes(q.toLowerCase()))
      : sites;
    const g = {};
    for (const s of filtered) {
      const key = s.group || "Sans regroupement";
      (g[key] = g[key] || []).push(s);
    }
    return g;
  }, [sites, q]);

  if (loading) return null;
  if (sites.length === 0) return null;

  return (
    <div className="relative" data-testid="site-switcher">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="site-switcher-button"
        className="min-h-[36px] px-3 flex items-center gap-2 border border-black bg-white hover:bg-black hover:text-white transition-colors text-[11px] font-mono uppercase tracking-widest"
      >
        <Globe className="w-3.5 h-3.5" strokeWidth={1.75} />
        <span className="max-w-[140px] truncate normal-case font-serif">
          {current ? current.name : "Sélectionner un site"}
        </span>
        <ChevronsUpDown className="w-3 h-3 opacity-60" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 w-[280px] bg-white border border-black shadow-xl z-50 max-h-[70vh] overflow-y-auto"
               data-testid="site-switcher-menu">
            {sites.length > 5 && (
              <div className="p-2 border-b border-[var(--border-default)] flex items-center gap-2">
                <Search className="w-3.5 h-3.5 text-[var(--text-muted)]" strokeWidth={1.75} />
                <input
                  autoFocus type="text" placeholder="Rechercher…"
                  value={q} onChange={(e) => setQ(e.target.value)}
                  data-testid="site-switcher-search"
                  className="flex-1 text-sm font-mono outline-none placeholder:text-[var(--text-muted)]"
                />
              </div>
            )}
            {Object.entries(grouped).map(([groupName, items]) => (
              <div key={groupName}>
                {Object.keys(grouped).length > 1 && (
                  <div className="px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] bg-[var(--bg-app)]">
                    {groupName}
                  </div>
                )}
                {items.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => { setCurrentId(s.id); setOpen(false); setQ(""); }}
                    data-testid={`site-switcher-item-${s.id}`}
                    className={`w-full px-3 py-2 flex items-center gap-2 text-left hover:bg-[var(--bg-app)] transition-colors ${s.id === currentId ? "bg-black/5" : ""} ${!s.active ? "opacity-50" : ""}`}
                  >
                    <Check className={`w-3.5 h-3.5 flex-shrink-0 ${s.id === currentId ? "opacity-100" : "opacity-0"}`} strokeWidth={2} />
                    <div className="flex-1 min-w-0">
                      <div className="font-serif truncate">{s.name}</div>
                      <div className="text-[10px] font-mono text-[var(--text-muted)] truncate">
                        {s.feed_count} flux{!s.active && " · inactif"}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
